import * as Sentry from '@sentry/browser';
import { put } from 'redux-saga/effects';
import { HTTP_STATUSES } from '@/constants';

/**
 * Saga to fetch data from an endpoint with a continuation token. The endpoint should consume
 * a `contToken` query string parameter that takes in the continuation token of the page to
 * fetch, and the endpoint should return a `contToken` field in the body of a successful response
 * that contains either the continuation token of the next page if the there is
 * a next page available.
 *
 * This saga will dispatch an action created by the provided action
 * creator every time a page is retrieved. The action payload will contain a `pageItems`
 * field that can have zero or more data items extracted from the response, and
 * an `inFlight` that is set to true if there are more pages to read.
 *
 * @param {BaseSender} config.sender An API sender instance whose `getV2` method will fetch the data
 * @param {string} config.path API endpoint to call
 * @param {Function} config.getItemsFromResponseData Function that takes the body of the API's
 * JSON response and returns an array of data items/rows
 * @param {Function} config.serverReturnedPageAction Action creator for an action to dispatch
 * with every page retrieved
 * @param {Function} config.serverReturnedSaga Saga to call with every page returned.
 * Mutually-exclusive with `serverReturnedPageAction`
 * @param {Record<string, string | number>} config.queryParams Query string parameters to
 * be passed to the API endpoint
 * @param {boolean?} config.fetchAllPages Set this to true if all available
 * pages need to be retrieved
 * @param {number?} config.numPagesToFetch Set this to a number of pages to retrieve only up to that
 * number of pages. Mututally-exclusive with `config.fetchAllPages`
 * @param {number} config.maxRetries Maximum number of times to retry a failed request.
 * @param {any} config.contToken Continuation token of the next page to request.
 * Pass this in the first call to start reading from a specific known page.
 * @param {number} config.numRetriesPerformed Used by recursive calls only - contains number
 * of times the current page's request has been retried so far due to an error
 * @param {(err: Error, numRetriesPerformed: number) => void} config.onRetryWithError Optional
 * function to call when the saga is retrying to fetch the same page due to an error
 */
export function* getPaginatedData(config) {
  const {
    sender,
    path,
    getItemsFromResponseData,
    serverReturnedPageAction,
    serverReturnedSaga,
    queryParams = {},
    fetchAllPages = true,
    numPagesToFetch = null,
    maxRetries = 1,
    contToken = null,
    numRetriesPerformed = 0,
    onRetryWithError,
  } = config;

  const returnPageResult =
    serverReturnedSaga ?? ((...args) => put(serverReturnedPageAction(...args)));

  const allQueryParams = {
    ...queryParams,
    ...(contToken && { contToken }),
  };

  const allRequestParams = {
    queryParams: allQueryParams,
    validateStatus: false,
  };

  if (Number.isInteger(numPagesToFetch) && numPagesToFetch < 1) {
    yield returnPageResult({ pageItems: [], inFlight: false });
    return;
  }

  try {
    const response = yield sender.getV2(path, allRequestParams);
    const responseData = response?.data;

    const nextPageContToken = responseData?.contToken;

    if (response.status === HTTP_STATUSES.NOT_FOUND) {
      yield returnPageResult({ pageItems: [], inFlight: false });
      return;
    }

    const pageItems = getItemsFromResponseData(response?.data);

    const shouldFetchMorePages = Boolean(
      nextPageContToken && (fetchAllPages || numPagesToFetch > 1)
    );
    yield returnPageResult({ pageItems, inFlight: shouldFetchMorePages });

    if (shouldFetchMorePages) {
      yield getPaginatedData({
        ...config,
        contToken: nextPageContToken,
        numPagesToFetch: !fetchAllPages ? numPagesToFetch - 1 : null,
        numRetriesPerformed: 0,
      });
    }
  } catch (err) {
    const shouldRetry = numRetriesPerformed < maxRetries;

    if (onRetryWithError && shouldRetry) {
      yield onRetryWithError(err, numRetriesPerformed);
    }

    if (!shouldRetry) {
      Sentry.captureException(err);
      throw err;
    }

    yield getPaginatedData({
      ...config,
      numRetriesPerformed: numRetriesPerformed + 1,
    });
  }
}

/**
 * Creates a reducer function (i.e. (state, action) => newState)) that
 * inserts a new page of data sent by the `getPaginatedData` saga
 * into an array at a specific location in the store
 * @param {string[]} collectionKeyPath An array of fields forming a "path" of fields
 * that needs to be traversed to reach the array of paged items in the store. For example,
 * if we store our data at `store.getState().foo.bar.propertiesList`, then this path
 * should be ['foo', 'bar', 'propertiesList'].
 * See also: https://immutable-js.com/docs/v4.0.0/Record/#setIn()
 */
export const paginatedDataReturnedReducerFactory =
  (collectionKeyPath) => (state, action) => {
    const { pageItems } = action.payload;

    const currentCollection = state.getIn(collectionKeyPath);
    if (!currentCollection) {
      // collection in the state was null - initialize it
      return state.setIn(collectionKeyPath, pageItems);
    }
    // if collection in the state was not null, it means the payload is the continuation
    // of the existing collection (fetched using contToken)
    const newCollection = currentCollection.concat(pageItems);
    return state.setIn(collectionKeyPath, newCollection);
  };

export function* getGraphqlQueryData(config) {
  const {
    variables,
    execFunction,
    serverReturnedPageAction,
    serverReturnedSaga,
  } = config;

  const returnPageResult =
    serverReturnedSaga ?? ((...args) => put(serverReturnedPageAction(...args)));

  try {
    const response = yield execFunction(variables);
    yield returnPageResult(response);
  } catch (err) {
    Sentry.captureException(err);
    throw err;
  }
}
